/**
 * notifications.js — FCM Token Management & Foreground Push
 *
 * Handles:
 *   - Requesting notification permission + FCM token
 *   - Saving/clearing token in Firestore
 *   - Foreground message listener
 *
 * VAPID key must be generated once from Firebase Console:
 *   Project Settings > Cloud Messaging > Web Push certificates > Generate key pair
 */

import { messaging } from '../firebase';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * Request notification permission, get FCM token, save to Firestore.
 *
 * @param {string} uid - Firebase Auth user ID
 * @returns {Promise<string|null>} FCM token or null if denied/unsupported
 */
export async function requestNotificationPermission(uid) {
  if (!messaging) {
    console.warn('[notifications] FCM not supported in this browser');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await updateDoc(doc(db, 'users', uid), {
        fcm_token: token,
        notifications_enabled: true,
      });
      return token;
    }
  } catch (err) {
    console.error('[notifications] Failed to get token:', err);
  }
  return null;
}

/**
 * Disable notifications — clear token from Firestore and unregister.
 *
 * @param {string} uid - Firebase Auth user ID
 */
export async function disableNotifications(uid) {
  try {
    if (messaging) {
      await deleteToken(messaging);
    }
    await updateDoc(doc(db, 'users', uid), {
      fcm_token: null,
      notifications_enabled: false,
    });
  } catch (err) {
    console.error('[notifications] Failed to disable:', err);
  }
}

/**
 * Listen for foreground push messages.
 *
 * @param {Function} callback - Called with { title, body } for each message
 * @returns {Function} Unsubscribe function
 */
export function onForegroundMessage(callback) {
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    const { title, body } = payload.notification || {};
    callback({ title: title || 'Spendable', body: body || '' });
  });
}
